<template>
    <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
        <!-- Header with Icon and Title -->
        <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <div class="flex items-center space-x-3">
                <div :class="[
                    'p-2 rounded-lg',
                    iconBgClass
                ]">
                    <component :is="iconComponent" :class="['w-5 h-5', iconColorClass]" />
                </div>
                <h3 class="text-lg font-semibold text-secondary-900 dark:text-white">{{ title }}</h3>
            </div>
        </div>

        <!-- Stats -->
        <div class="px-6 py-4 bg-secondary-50 dark:bg-secondary-900/30">
            <div class="grid grid-cols-2 gap-4">
                <div>
                    <p class="text-2xl font-bold text-secondary-900 dark:text-white">{{ stats.total }}</p>
                    <p class="text-xs text-secondary-500 dark:text-secondary-400 mt-1">Total Raised</p>
                </div>
                <div>
                    <p class="text-2xl font-bold" :class="textColorClass">{{ stats.inProgress }}</p>
                    <p class="text-xs text-secondary-500 dark:text-secondary-400 mt-1">In Progress</p>
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="px-6 py-4 flex space-x-3">
            <Link
                :href="links.viewAll"
                class="flex-1 text-center px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md text-sm font-medium text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
            >
                View All
            </Link>
            <Link
                :href="links.create"
                :class="[
                    'flex-1 text-center px-4 py-2 rounded-md text-sm font-medium text-white transition-colors',
                    buttonBgClass
                ]"
            >
                Create New
            </Link>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { TicketIcon, WrenchIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    stats: {
        type: Object,
        required: true,
    },
    icon: {
        type: String,
        required: true,
    },
    color: {
        type: String,
        default: 'blue',
    },
    links: {
        type: Object,
        required: true,
    },
});

const iconComponent = computed(() => {
    const icons = {
        'TicketIcon': TicketIcon,
        'WrenchIcon': WrenchIcon,
        'ExclamationTriangleIcon': ExclamationTriangleIcon,
    };
    return icons[props.icon] || TicketIcon;
});

const iconBgClass = computed(() => {
    const classes = {
        'blue': 'bg-blue-100 dark:bg-blue-900/30',
        'green': 'bg-green-100 dark:bg-green-900/30',
        'red': 'bg-red-100 dark:bg-red-900/30',
        'purple': 'bg-purple-100 dark:bg-purple-900/30',
        'yellow': 'bg-yellow-100 dark:bg-yellow-900/30',
    };
    return classes[props.color] || classes.blue;
});

const iconColorClass = computed(() => {
    const classes = {
        'blue': 'text-blue-600 dark:text-blue-400',
        'green': 'text-green-600 dark:text-green-400',
        'red': 'text-red-600 dark:text-red-400',
        'purple': 'text-purple-600 dark:text-purple-400',
        'yellow': 'text-yellow-600 dark:text-yellow-400',
    };
    return classes[props.color] || classes.blue;
});

const textColorClass = computed(() => {
    const classes = {
        'blue': 'text-blue-600 dark:text-blue-400',
        'green': 'text-green-600 dark:text-green-400',
        'red': 'text-red-600 dark:text-red-400',
        'purple': 'text-purple-600 dark:text-purple-400',
        'yellow': 'text-yellow-600 dark:text-yellow-400',
    };
    return classes[props.color] || classes.blue;
});

const buttonBgClass = computed(() => {
    const classes = {
        'blue': 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
        'green': 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600',
        'red': 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
        'purple': 'bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600',
        'yellow': 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600',
    };
    return classes[props.color] || classes.blue;
});
</script>
